import * as React from 'react';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Dashboard from '../../../components/Admin/Dashboard.js';
import { useState, useEffect } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Link } from 'react-router-dom';
import { Styles } from '../../../styles/global.js';
import {
  getChatbotSummary,
  getDisplayRequestLink,
  getInstructors,
  getMainConfig,
  getRooms,
  getCampusMapLink,
  updateDisplayRequest,
  updateDisplayRequestLink,
  updateInstructors,
  updateKnowledgeBase,
  updateMainConfig,
  updateProjectInstructors,
  updateProjectRooms,
  updateStations,
  updateCampusMapLink,
} from '../../../utils/API.js';
import 'chart.js/auto';
import Modal from '../../../components/Modal.js';
import dayjs from 'dayjs';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import {
  Alert,
  Checkbox,
  TextField,
  Typography,
  Divider,
  Button,
  AccordionDetails,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { formatDate } from '../../../utils/dates.js';
import useFeedback from '../../../utils/FeedbackContext/useFeedback.js';

function ViewAndEditMisc({ setMainConfig, mainConfig }) {
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [chatbotSummary, setChatbotSummary] = useState(null);
  const [file, setFile] = useState(null);
  const { notifyError, notifySuccess } = useFeedback();
  const handleSubmission = async () => {
    try {
      await updateMainConfig(mainConfig);
      notifySuccess('Updated Successfully');
    } catch (error) {
      notifyError('Error Updating');
      console.error(error?.response?.data || 'Error Updating');
    }
  };
  const handleUpdate = async () => {
    setLoader(true);

    const formData = new FormData();
    formData.append('pdf', file);
    try {
      const response = await updateKnowledgeBase(formData);

      if (response?.data?.success) {
        notifySuccess('Chatbot knowledge base updated successfully!');
      }
      setLoader(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      notifyError(error?.response?.data || 'Error Updating');
      setLoader(false);
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const openChatbotSummary = () => {
    setOpen(true);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const chatbotSummary = await getChatbotSummary();
        setChatbotSummary(chatbotSummary?.data?.data);
      } catch (error) {
        console.error(error);
      }
    }
    fetchData();
  }, []);

  return (
    mainConfig && (
      <Box padding={2}>
        <Modal open={open} handleClose={() => setOpen(false)} cta="Close">
          <Typography variant="h6" fontWeight={'fontWeightBold'}>
            Chatbot Summary
          </Typography>

          {chatbotSummary && (
            <AccordionDetails>
              <Typography variant="body1">
                {chatbotSummary?.knowledgeBase}
              </Typography>
            </AccordionDetails>
          )}
        </Modal>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography variant="h6" fontWeight={'fontWeightBold'}>
            Miscellaneous Configurations
          </Typography>
          <Box>
            <Button variant="text" color="primary" onClick={openChatbotSummary}>
              View Chatbot Summary
            </Button>
            <Button variant="text" color="primary" onClick={handleSubmission}>
              Save
            </Button>
          </Box>
        </Box>

        {/* Link Checking */}
        <Box
          display={'flex'}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            marginY: 2,
          }}
        >
          <Typography variant="body1">
            <b>Link Checking for Poster Links</b>
          </Typography>
          {mainConfig?.misc && (
            <Checkbox
              checked={mainConfig?.misc?.linkChecking}
              onChange={(e) => {
                mainConfig.misc.linkChecking = e.target.checked;
                setMainConfig({ ...mainConfig });
              }}
            />
          )}
        </Box>

        {mainConfig?.misc?.linkChecking && (
          <Alert severity="info" sx={{ marginY: 2 }}>
            Poster Links are now being checked! They must point to a valid
            submission in a UMD Google Drive Folder.
          </Alert>
        )}

        {/* Enable ChatBot */}
        <Box
          display={'flex'}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            marginY: 2,
          }}
        >
          <Typography variant="body1">
            <b>Enable Chatbot</b>
          </Typography>
          {mainConfig?.misc && (
            <Checkbox
              checked={mainConfig?.misc?.showChatBot}
              onChange={(e) => {
                mainConfig.misc.showChatBot = e.target.checked;
                setMainConfig({ ...mainConfig });
              }}
            />
          )}
        </Box>

        {mainConfig?.misc?.showChatBot && (
          <Alert severity="info" sx={{ marginY: 2 }}>
            Chatbot is now enabled and visible to home and event pages!
          </Alert>
        )}

        {/* Update ChatBot */}
        <Box
          display={'flex'}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            marginY: 2,
          }}
        >
          <Typography variant="body1">
            <b>Update Chatbot</b>
          </Typography>
          <Box display={'flex'} gap={2}>
            <label htmlFor="contained-button-file">
              <input
                accept="application/pdf"
                id="contained-button-file"
                multiple
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
              >
                Upload
              </Button>
            </label>
            {!loader ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdate}
                disabled={!file}
              >
                Update
              </Button>
            ) : (
              <CircularProgress />
            )}
          </Box>
        </Box>
        <Alert severity="info" sx={{ marginY: 2 }}>
          Reads PDF files and updates the chatbot knowledge base.
          {file && ' File: ' + file?.name}
        </Alert>
      </Box>
    )
  );
}

function ViewAndEditInfoBanner({ setMainConfig, mainConfig }) {
  const { notifyError, notifySuccess } = useFeedback();
  const handleSubmission = async () => {
    try {
      await updateMainConfig(mainConfig);
      notifySuccess('Updated Successfully');
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
      console.error(error);
      notifyError(error?.response?.data || 'Error Updating');
    }
  };

  return (
    mainConfig && (
      <Box padding={2}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography variant="h6" fontWeight={'fontWeightBold'}>
            Info Banner for Guest Pages
          </Typography>
          <Button variant="text" color="primary" onClick={handleSubmission}>
            Save
          </Button>
        </Box>
        <Box
          display={'flex'}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            marginY: 2,
          }}
        >
          <Typography variant="body1">
            <b>Show Alert</b>
          </Typography>
          {mainConfig?.infoBanner && (
            <Checkbox
              checked={mainConfig?.infoBanner?.showAlert}
              onChange={(e) => {
                mainConfig.infoBanner.showAlert = e.target.checked;
                setMainConfig({ ...mainConfig });
              }}
            />
          )}
        </Box>

        {mainConfig?.infoBanner?.showAlert && (
          <Alert severity="info" sx={{ marginY: 2 }}>
            Info Banner is now visible!
          </Alert>
        )}

        <Typography variant="body1">
          <b>Message</b>
        </Typography>

        <TextField
          value={mainConfig?.infoBanner?.message}
          onChange={(e) => {
            mainConfig.infoBanner.message = e.target.value;
            setMainConfig({ ...mainConfig });
          }}
          sx={{ width: '100%' }}
          disabled={mainConfig?.infoBanner?.showAlert ? false : true}
        />
      </Box>
    )
  );
}

function EditPosterTemplates({ open, setOpen, setMainConfig, mainConfig }) {
  const { notifyError, notifySuccess } = useFeedback();
  const handleClose = async () => {
    setOpen(false);
    try {
      await updateMainConfig(mainConfig);
      notifySuccess('Updated Successfully');
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
      console.error(error);
    }
  };

  return (
    <Modal open={open} handleClose={handleClose} cta="Save">
      <Typography variant="h6" fontWeight={'fontWeightBold'}>
        Poster Template Links
      </Typography>
      {mainConfig?.posterTemplatesLinks?.map((link, index) => (
        <div key={index}>
          <Typography variant="body1">
            <b>{link.name}</b>
          </Typography>
          <TextField
            label="Link"
            value={link.link}
            onChange={(e) => {
              link.link = e.target.value;
              mainConfig.posterTemplatesLinks[index] = link;
              setMainConfig({ ...mainConfig });
            }}
            sx={{ margin: 2 }}
          />
          <Divider sx={{ marginY: 1 }} />
        </div>
      ))}
      <Divider sx={{ marginY: 1 }} />
    </Modal>
  );
}

function ViewPosterTemplates({ setMainConfig, mainConfig }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {mainConfig && (
        <Box padding={2} overflow={'hidden'}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Typography variant="h6" fontWeight={'fontWeightBold'}>
              Poster Template Links
            </Typography>
            <Button
              variant="text"
              color="primary"
              onClick={() => setOpen(true)}
            >
              Edit
            </Button>
          </Box>
          {mainConfig?.posterTemplatesLinks?.map((link, index) => (
            <div key={index}>
              <Typography variant="body1">
                <b>{link.name}</b>
              </Typography>
              <Link to={`${link.link}`} target="_blank">
                {link.link}
              </Link>
              <Divider sx={{ marginY: 1 }} />
            </div>
          ))}
        </Box>
      )}
      {open && (
        <EditPosterTemplates
          {...{ setMainConfig, mainConfig, open, setOpen }}
        />
      )}
    </>
  );
}

const EditInstructors = ({
  open,
  setOpen,
  instructors,
  setInstructors,
  handleSave,
}) => {
  return (
    <Modal open={open} handleClose={() => setOpen(false)}>
      <Typography variant="h6" fontWeight={'fontWeightBold'}>
        Edit Instructors
      </Typography>
      {instructors?.map((instructor, index) => (
        <div key={index}>
          <Typography variant="body1">
            <b>{instructor.department}</b>
          </Typography>
          <TextField
            label="Names"
            value={instructor.names}
            onChange={(e) => {
              instructor.names = e.target.value;
              instructors[index] = instructor;
              setInstructors([...instructors]);
            }}
            sx={{ margin: 2 }}
            fullWidth
          />
          <Divider sx={{ marginY: 1 }} />
        </div>
      ))}
      <Button
        sx={{ margin: 2 }}
        onClick={handleSave}
        variant="outlined"
        fullWidth
      >
        Save Changes
      </Button>
    </Modal>
  );
};

const EditStations = ({ open, setOpen, stations, setStations, handleSave }) => {
  return (
    <Modal open={open} handleClose={() => setOpen(false)}>
      <Typography variant="h6" fontWeight={'fontWeightBold'} mt={2}>
        Edit Stations
      </Typography>
      <Grid container spacing={2}>
        {stations?.map((station, index) => (
          <Grid item xs={12} sm={6} lg={4} key={index}>
            <Box>
              <Typography variant="body1">
                <b>{station.key}</b>
              </Typography>
              <TextField
                label=""
                value={station.value}
                onChange={(e) => {
                  station.value = e.target.value;
                  stations[index] = station;
                  setStations([...stations]);
                }}
                sx={{ marginY: 2 }}
                fullWidth
              />
              <Divider sx={{ marginY: 1 }} />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Button
        sx={{ margin: 2 }}
        onClick={handleSave}
        variant="outlined"
        fullWidth
      >
        Save Changes
      </Button>
    </Modal>
  );
};

function UpdateInstructors({ open, setOpen }) {
  const { notifyError, notifySuccess } = useFeedback();

  const handleUpdate = async () => {
    try {
      await updateProjectInstructors();
      notifySuccess('Projects from this year updated to latest instructors');
      setOpen(false);
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
    }
  };

  return (
    <Modal open={open} handleClose={() => setOpen(false)}>
      <Typography variant="h6" fontWeight={'fontWeightBold'}>
        Update Instructors
      </Typography>
      <Alert severity="info" sx={{ marginY: 2 }}>
        Updating instructors will update all projects from this current calender
        year with the new instructors. Ensure the instructors are correct before
        updating.
      </Alert>
      <Divider sx={{ marginY: 1 }} />

      <Button fullWidth variant="outlined" onClick={handleUpdate}>
        Confirm Update Instructors
      </Button>
    </Modal>
  );
}

function UpdateStations({ open, setOpen }) {
  const { notifyError, notifySuccess } = useFeedback();

  const handleUpdate = async () => {
    try {
      await updateProjectRooms();
      notifySuccess(
        'Projects from this year updated to latest building and rooms',
      );
      setOpen(false);
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
    }
  };

  return (
    <Modal open={open} handleClose={() => setOpen(false)}>
      <Typography variant="h6" fontWeight={'fontWeightBold'}>
        Update Instructors
      </Typography>
      <Alert severity="info" sx={{ marginY: 2 }}>
        Updating building and rooms will update all projects from this current
        calender year with the new (building/rooms). Ensure the building and
        rooms are correct before updating.
      </Alert>
      <Divider sx={{ marginY: 1 }} />

      <Button fullWidth variant="outlined" onClick={handleUpdate}>
        Confirm
      </Button>
    </Modal>
  );
}

function InstructorsConfig() {
  const [editOpen, setEditOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const { notifyError, notifySuccess } = useFeedback();

  const handleSave = async () => {
    setUpdateOpen(false);
    try {
      await updateInstructors(instructors);
      notifySuccess(
        'Instructors Updated Successfully. Need to perform a "Update all Projects" to reflect changes to all projects. However, new projects will use the updated instructors.',
      );
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
      console.error(error);
    }
    setEditOpen(false);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getInstructors();
        setInstructors(response?.data?.data);
      } catch (error) {
        console.error(error);
      }
    }
    fetchData();
  }, []);

  return (
    !!instructors?.length && (
      <>
        <Box padding={2} overflow={'hidden'}>
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            flexDirection={{ xs: 'column', md: 'row' }}
          >
            <Typography variant="h6" fontWeight={'fontWeightBold'}>
              Instructors
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setUpdateOpen(true)}
              >
                Update all Projects
              </Button>
              <Button
                variant="text"
                color="primary"
                onClick={() => setEditOpen(true)}
              >
                Edit Instructors
              </Button>
            </Box>
          </Box>
          {instructors?.map((instructor, index) => (
            <div key={index}>
              <Typography variant="body1">
                <b>{instructor.department}</b>
              </Typography>
              <Typography variant="body2">{instructor.names}</Typography>
              <Divider sx={{ marginY: 1 }} />
            </div>
          ))}

          {editOpen && (
            <EditInstructors
              open={editOpen}
              setOpen={setEditOpen}
              instructors={instructors}
              setInstructors={setInstructors}
              handleSave={handleSave}
            />
          )}

          {updateOpen && (
            <UpdateInstructors open={updateOpen} setOpen={setUpdateOpen} />
          )}
        </Box>
      </>
    )
  );
}

function StationsConfig() {
  const [editOpen, setEditOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [stations, setStations] = useState([]);
  const { notifyError, notifySuccess } = useFeedback();

  const handleSave = async () => {
    setUpdateOpen(false);
    try {
      await updateStations({ rooms: stations });
      notifySuccess(
        'Buildings/Rooms Updated Successfully. Need to perform a "Update all Projects" to reflect changes to all projects.',
      );
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
      console.error(error);
    }
    // notifySuccess('Stations has been saved');
    setEditOpen(false);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getRooms();
        setStations(response?.data?.data);
      } catch (error) {
        console.error(error);
      }
    }
    fetchData();
  }, []);

  return (
    !!stations?.length && (
      <>
        <Box padding={2} overflow={'hidden'}>
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            flexDirection={{ xs: 'column', md: 'row' }}
          >
            <Typography variant="h6" fontWeight={'fontWeightBold'}>
              Stations (Building and Rooms)
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setUpdateOpen(true)}
              >
                Update all Projects
              </Button>
              <Button
                variant="text"
                color="primary"
                onClick={() => setEditOpen(true)}
              >
                Edit Stations
              </Button>
            </Box>
          </Box>
          {stations?.map((station, index) => (
            <div key={index}>
              <Typography variant="body1">
                <b>{station.key}</b>
              </Typography>
              <Typography variant="body2">{station.value || 'TBD'}</Typography>
              <Divider sx={{ marginY: 1 }} />
            </div>
          ))}

          {editOpen && (
            <EditStations
              open={editOpen}
              setOpen={setEditOpen}
              stations={stations}
              setStations={setStations}
              handleSave={handleSave}
            />
          )}

          {updateOpen && (
            <UpdateStations open={updateOpen} setOpen={setUpdateOpen} />
          )}
        </Box>
      </>
    )
  );
}

function EditGoogleDriveSubmissionConfig({
  setMainConfig,
  mainConfig,
  open,
  setOpen,
}) {
  const { notifyError, notifySuccess } = useFeedback();
  const handleClose = async () => {
    setOpen(false);
    try {
      await updateMainConfig(mainConfig);
      notifySuccess('Updated Successfully');
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
      console.error(error);
    }
  };

  return (
    <Modal open={open} handleClose={handleClose} cta="Save">
      <Typography variant="h6" fontWeight={'fontWeightBold'}>
        Google Drive Poster Submission Links
      </Typography>
      {mainConfig?.googleDriveFolders?.map((link, index) => (
        <div key={index}>
          <Typography variant="body1">
            <b>{link[(index, 0)]}</b>
          </Typography>
          <TextField
            label="Link"
            value={link[(index, 1)]}
            onChange={(e) => {
              link[(index, 1)] = e.target.value;
              mainConfig.googleDriveFolders[index] = link;
              setMainConfig({ ...mainConfig });
            }}
            sx={{ margin: 2 }}
          />
          <Divider sx={{ marginY: 1 }} />
        </div>
      ))}
      <Divider sx={{ marginY: 1 }} />
    </Modal>
  );
}

function ViewGoogleDriveSubmissionConfig({ setMainConfig, mainConfig }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {mainConfig && (
        <Box padding={2}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Typography variant="h6" fontWeight={'fontWeightBold'}>
              Google Drive Poster Submission Links
            </Typography>
            <Button
              variant="text"
              color="primary"
              onClick={() => setOpen(true)}
            >
              Edit
            </Button>
          </Box>
          {mainConfig?.googleDriveFolders?.map((link, index) => (
            <div key={index}>
              <Typography variant="body1">
                <b>{link[(index, 0)]}</b>
              </Typography>
              <Link to={`${link[(index, 1)]}`} target="_blank">
                {link[(index, 1)]}
              </Link>
              <Divider sx={{ marginY: 1 }} />
            </div>
          ))}
        </Box>
      )}
      {open && (
        <EditGoogleDriveSubmissionConfig
          {...{ setMainConfig, mainConfig, open, setOpen }}
        />
      )}
    </>
  );
}

function EditDeadlineConfig({
  open,
  setOpen,
  dates,
  setMainConfig,
  mainConfig,
}) {
  const { notifyError, notifySuccess } = useFeedback();
  const handleClose = async () => {
    setOpen(false);
    try {
      await updateMainConfig(mainConfig);
      notifySuccess('Updated Successfully');
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
      console.error(error);
    }
  };

  return (
    <Modal open={open} handleClose={handleClose} cta="Save">
      <Typography variant="h6" fontWeight={'fontWeightBold'}>
        Deadlines
      </Typography>
      <Typography variant="body1">
        <b>Abstract Submission Deadline</b>
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dayjs(dates?.abstract)}
          onChange={(newValue) => {
            dates.abstract = newValue.toISOString();
            setMainConfig({ ...mainConfig, dueDates: dates });
          }}
        />
      </LocalizationProvider>
      <Divider sx={{ marginY: 1 }} />

      <Typography variant="body1">
        <b>Display Request Deadline</b>
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dayjs(dates?.displayRequest)}
          onChange={(newValue) => {
            dates.displayRequest = newValue.toISOString();
            setMainConfig({ ...mainConfig, dueDates: dates });
          }}
        />
      </LocalizationProvider>
      <Divider sx={{ marginY: 1 }} />

      <Typography variant="body1">
        <b>Draft Poster Submission Deadline: Poster File (.pdf)</b>
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dayjs(dates?.posterLink)}
          onChange={(newValue) => {
            dates.posterLink = newValue.toISOString();
            setMainConfig({ ...mainConfig, dueDates: dates });
          }}
        />
      </LocalizationProvider>
      <Divider sx={{ marginY: 1 }} />

      <Typography variant="body1">
        <b>Draft Poster Submission Deadline: Poster Image</b>
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dayjs(dates?.posterImage)}
          onChange={(newValue) => {
            dates.posterImage = newValue.toISOString();
            setMainConfig({ ...mainConfig, dueDates: dates });
          }}
        />
      </LocalizationProvider>
      <Divider sx={{ marginY: 1 }} />

      <Typography variant="body1">
        <b>Final Poster Submission Deadline: Poster File (.pdf)</b>
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dayjs(dates?.posterLinkFinal)}
          onChange={(newValue) => {
            dates.posterLinkFinal = newValue.toISOString();
            setMainConfig({ ...mainConfig, dueDates: dates });
          }}
        />
      </LocalizationProvider>
      <Divider sx={{ marginY: 1 }} />

      <Typography variant="body1">
        <b>Final Project Submission Deadline: Poster Image</b>
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dayjs(dates?.posterImageFinal)}
          onChange={(newValue) => {
            dates.posterImageFinal = newValue.toISOString();
            setMainConfig({ ...mainConfig, dueDates: dates });
          }}
        />
      </LocalizationProvider>
      <Divider sx={{ marginY: 1 }} />

      <Typography variant="body1">
        <b>Senior Design Day</b>
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dayjs(dates?.seniorDesignDay)}
          onChange={(newValue) => {
            dates.seniorDesignDay = newValue.toISOString();
            setMainConfig({ ...mainConfig, dueDates: dates });
          }}
        />
      </LocalizationProvider>
      <Divider sx={{ marginY: 1 }} />
    </Modal>
  );
}

function ViewDeadlineConfig({ dates, setMainConfig, mainConfig }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {dates && (
        <Box padding={2}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Typography variant="h6" fontWeight={'fontWeightBold'}>
              Deadlines
            </Typography>
            <Button
              variant="text"
              color="primary"
              onClick={() => setOpen(true)}
            >
              Edit
            </Button>
          </Box>
          <Typography variant="body1">
            <b>Abstract Submission Deadline</b>
          </Typography>
          <Typography variant="body2">
            {formatDate(dates?.abstract, true)}
          </Typography>

          <Divider sx={{ marginY: 1 }} />

          <Typography variant="body1">
            <b>Display Requests Submission Deadline</b>
          </Typography>
          <Typography variant="body2">
            {formatDate(dates?.displayRequest, true)}
          </Typography>

          <Divider sx={{ marginY: 1 }} />

          <Typography variant="body1">
            <b>Draft Poster Submission Deadline: Poster File (.pdf)</b>
          </Typography>
          <Typography variant="body2">
            {formatDate(dates?.posterLink, true)}
          </Typography>
          <Divider sx={{ marginY: 1 }} />

          <Typography variant="body1">
            <b>Draft Poster Submission Deadline: Poster Image</b>
          </Typography>
          <Typography variant="body2">
            {formatDate(dates?.posterImage, true)}
          </Typography>
          <Divider sx={{ marginY: 1 }} />

          <Typography variant="body1">
            <b>Final Poster Submission Deadline: Poster File (.pdf)</b>
          </Typography>
          <Typography variant="body2">
            {formatDate(dates?.posterLinkFinal, true)}
          </Typography>
          <Divider sx={{ marginY: 1 }} />

          <Typography variant="body1">
            <b>Final Project Submission Deadline: Poster Image</b>
          </Typography>
          <Typography variant="body2">
            {formatDate(dates?.posterImageFinal, true)}
          </Typography>
          <Divider sx={{ marginY: 1 }} />

          <Typography variant="body1">
            <b>Senior Design Day</b>
          </Typography>
          <Typography variant="body2">
            {formatDate(dates?.seniorDesignDay, true)}
          </Typography>
          <Divider sx={{ marginY: 1 }} />
        </Box>
      )}
      {open && (
        <EditDeadlineConfig
          open={open}
          setOpen={setOpen}
          dates={dates}
          setMainConfig={setMainConfig}
          mainConfig={mainConfig}
        />
      )}
    </>
  );
}

const EditPosterDisplayRequest = ({
  open,
  setOpen,
  link,
  setLinks,
  handleSave,
}) => {
  return (
    <Modal open={open} handleClose={() => setOpen(false)}>
      <Typography variant="h6" fontWeight={'fontWeightBold'}>
        Edit Display Request Link
      </Typography>
      <div key={'ind'}>
        <Typography variant="body1">Display Request Link</Typography>
        <TextField
          label="Display Request Link"
          value={link}
          onChange={(e) => {
            link = e.target.value;
            setLinks(link);
          }}
          sx={{ margin: 2 }}
          fullWidth
        />
        <Divider sx={{ marginY: 1 }} />
      </div>
      <Button
        sx={{ margin: 2 }}
        onClick={handleSave}
        variant="outlined"
        fullWidth
      >
        Save Changes
      </Button>
    </Modal>
  );
};

function PosterDisplayRequestConfig() {
  const [editOpen, setEditOpen] = useState(false);
  const [link, setLinks] = useState('');
  const { notifyError, notifySuccess } = useFeedback();

  const handleSave = async () => {
    try {
      await updateDisplayRequestLink(link);
      notifySuccess('Display Request Link Updated Successfully.');
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
      console.error(error);
    }
    setEditOpen(false);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getDisplayRequestLink();
        setLinks(response?.data?.data);
      } catch (error) {
        console.error(error);
      }
    }
    fetchData();
  }, []);

  return (
    <>
      <Box padding={2} overflow={'hidden'}>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          flexDirection={{ xs: 'column', md: 'row' }}
        >
          <Typography variant="h6" fontWeight={'fontWeightBold'}>
            Display Request
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="text"
              color="primary"
              onClick={() => setEditOpen(true)}
            >
              Edit Display Request
            </Button>
          </Box>
        </Box>
        {link && (
          <div>
            <Typography variant="body1">
              <b>Display Request Link</b>
            </Typography>
            <Typography variant="body2">{link}</Typography>
            <Divider sx={{ marginY: 1 }} />
          </div>
        )}

        {editOpen && (
          <EditPosterDisplayRequest
            open={editOpen}
            setOpen={setEditOpen}
            link={link}
            setLinks={setLinks}
            handleSave={handleSave}
          />
        )}
      </Box>
    </>
  );
}

function CampusMapLinksConfig() {
  const [editOpen, setEditOpen] = useState(false);
  const [link, setLinks] = useState('');
  const { notifyError, notifySuccess } = useFeedback();

  const handleSave = async () => {
    try {
      await updateCampusMapLink(link);
      notifySuccess('Campus Map Links Updated Successfully.');
    } catch (error) {
      notifyError(error?.response?.data || 'Error Updating');
      console.error(error);
    }
    setEditOpen(false);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getCampusMapLink();
        setLinks(response?.data?.data);
      } catch (error) {
        console.error(error);
      }
    }
    fetchData();
  }, []);

  const EditMapLinksRequest = ({
    open,
    setOpen,
    link,
    setLinks,
    handleSave,
  }) => {
    return (
      <Modal open={open} handleClose={() => setOpen(false)}>
        <Typography variant="h6" fontWeight={'fontWeightBold'}>
          Edit Campus Map Links
        </Typography>
        <div key={'ind'}>
          <FormGroup>
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Show Campus Map Links Widget to users?"
              checked={link?.visible}
              onChange={(e) => {
                link.visible = e.target.checked;
                setLinks({ ...link });
              }}
            />
          </FormGroup>

          {link?.links?.map((item, index) => (
            <div key={index}>
              <Typography variant="body1">
                <b>{item.key}</b>
              </Typography>
              <TextField
                label=""
                value={item.value}
                onChange={(e) => {
                  item.value = e.target.value;
                  link.links[index] = item;
                  setLinks({ ...link });
                }}
                sx={{ marginY: 2 }}
                fullWidth
              />
            </div>
          )) || null}

          <Divider sx={{ marginY: 1 }} />
        </div>
        <Button
          sx={{ margin: 2 }}
          onClick={handleSave}
          variant="outlined"
          fullWidth
        >
          Save Changes
        </Button>
      </Modal>
    );
  };

  return (
    <>
      <Box padding={2} overflow={'hidden'}>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          flexDirection={{ xs: 'column', md: 'row' }}
        >
          <Typography variant="h6" fontWeight={'fontWeightBold'}>
            Campus Map Links
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="text"
              color="primary"
              onClick={() => setEditOpen(true)}
            >
              Edit Campus Map Links
            </Button>
          </Box>
        </Box>
        {link && (
          <div>
            <Typography variant="body1">
              <b>Show this widget to users?</b>
            </Typography>
            <Typography variant="body2">
              {link.visible ? 'Yes ✅' : 'No 🟥 '}
            </Typography>
            <Divider sx={{ marginY: 1 }} />
          </div>
        )}

        {link?.links?.map((item, index) => (
          <div key={index}>
            <Typography variant="body1">
              <b>{item.key}</b>
            </Typography>
            <Typography variant="body2">{item.value}</Typography>
            <Divider sx={{ marginY: 1 }} />
          </div>
        )) || null}

        {editOpen && (
          <EditMapLinksRequest
            open={editOpen}
            setOpen={setEditOpen}
            link={link}
            setLinks={setLinks}
            handleSave={handleSave}
          />
        )}
      </Box>
    </>
  );
}

function AdminConfig() {
  let [mainConfig, setMainConfig] = useState({});
  useEffect(() => {
    async function fetchData() {
      try {
        const mainConfig = await getMainConfig();
        setMainConfig(mainConfig?.data?.data);
      } catch (error) {
        console.error(error);
      }
    }
    fetchData();
  }, []);
  return (
    <>
      <Box {...Styles.mainBox}>
        <Toolbar />

        {/* Container With Max Width = lg breakpoint */}
        <Container {...Styles.customContainer('md')}>
          {/*Outer Grid Container */}
          <Grid container spacing={3}>
            {/*Inner Grid Begins*/}

            {/*Inner Grid Item 1: Page Title */}
            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardPageWidget()}>
                Manage Configuration
              </Paper>
            </Grid>

            {/*Inner Grid Item 2:  View Deadline Config */}
            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <ViewDeadlineConfig
                  dates={mainConfig?.dueDates}
                  mainConfig={mainConfig}
                  setMainConfig={setMainConfig}
                />
              </Paper>
            </Grid>

            {/*Inner Grid Item 2:  View Google Drive Links Config */}
            {/* <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <ViewGoogleDriveSubmissionConfig
                  mainConfig={mainConfig}
                  setMainConfig={setMainConfig}
                />
              </Paper>
            </Grid> */}

            {/*Inner Grid Item 3:  View Poster Template Links */}
            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <ViewPosterTemplates
                  mainConfig={mainConfig}
                  setMainConfig={setMainConfig}
                />
              </Paper>
            </Grid>

            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <InstructorsConfig />
              </Paper>
            </Grid>

            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <StationsConfig />
              </Paper>
            </Grid>

            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <PosterDisplayRequestConfig />
              </Paper>
            </Grid>

            {/*Inner Grid Item 4:  View and Edit Info Banner*/}
            {/* <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <ViewAndEditInfoBanner
                  mainConfig={mainConfig}
                  setMainConfig={setMainConfig}
                />
              </Paper>
            </Grid> */}

            {/*Inner Grid Item 5:  View and Edit Msic*/}
            {/* <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <ViewAndEditMisc
                  mainConfig={mainConfig}
                  setMainConfig={setMainConfig}
                />
              </Paper>
            </Grid> */}

            <Grid item lg={12} sm={12} xs={12}>
              <Paper {...Styles.dashboardContentWidget('auto')}>
                <CampusMapLinksConfig />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default function AdminDashboardConfig() {
  return <Dashboard Page={AdminConfig} PageName="(A) Dashboard - Config" />;
}
